import React, {useEffect, useState} from 'react'
import {Box, Button, Center, Flex, Text, useDisclosure,} from '@chakra-ui/react'
import {useTranslation} from 'next-i18next'
import {FormProvider, useForm} from 'react-hook-form'
import {parseCookies} from 'nookies'
import Modal from '_app/components/Modal'
import Input from '_app/components/Form/Input'
import {CheckIcon} from '_app/components/Icons'
import {useApplyCouponToCartMutation} from '_app/graphql/mutations/cart/ApplyCouponToCart'
import {useRemoveCouponFromCartMutation} from '_app/graphql/mutations/cart/RemoveCouponFromCart'
import LinkButton from '_app/components/Buttons/LinkButton'
import toast from '_app/utils/toast'
import {cart} from '../../../_app/cache'

interface DiscountData {
    code: string;
}

const DiscountForm = ({ setData, data }) => {
    const {t} = useTranslation('checkout')
    const {isOpen, onOpen, onClose} = useDisclosure()
    const [applyCouponToCart] = useApplyCouponToCartMutation()
    const [removeCouponFromCart] = useRemoveCouponFromCartMutation()
    const [isValidDiscountCode, setIsValidDiscountCode] = useState<boolean | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const methods = useForm<DiscountData>()
    const { handleSubmit } = methods
    const handleRemoveCoupon = () => {
        setIsSubmitting(true)
        removeCouponFromCart({
            variables: {
                cart_id: parseCookies()?.cartId,
            },
            onCompleted: (res): any => {
                if (res) {
                    cart(res?.removeCouponFromCart)
                    setData(res?.removeCouponFromCart)
                }
            }
        })
            .then(() => {
                methods.reset()
                toast({
                    description: t('DISCOUNT_CODE_REMOVED_FROM_CART'),
                    status: 'success',
                })
                setIsValidDiscountCode(null)

        setIsSubmitting(false)
        onClose()
      })
      .catch(() => {
        setIsSubmitting(false)
      })
  }

  const discountCodeSubmitHandler = (data: { code: string }) => {
    if (!!data?.code) {
      setIsLoading(true)
      applyCouponToCart({
        variables: {
          cart_id: parseCookies()?.cartId,
          coupon_code: data?.code.trim(),
        },
          onCompleted: (res): any => {
              if (res) {
                  cart(res?.applyCouponToCart)
                  setData(res?.applyCouponToCart)
              }
          }
      })
        .then(() => {
          toast({
            description: t('DISCOUNT_CODE_ADDED_TO_CART'),
            status: 'success',
          })
          setIsValidDiscountCode(true)
          setIsLoading(false)
        })
        .catch(() => {
          setIsValidDiscountCode(false)
          setIsLoading(false)
        })
    } else {
      setIsValidDiscountCode(false)
    }
  }
  useEffect(() => {
    if (data?.cart?.applied_coupons?.[0]?.code && !methods.watch('code')) {
      methods.reset({ code: data?.cart?.applied_coupons?.[0]?.code })
    } else if (!data?.cart?.applied_coupons?.[0]?.code) {
      methods.reset()
      setIsValidDiscountCode(null)
    }
  }, [data, methods])
  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        maxWidth={['90%', null, '500px']}
      >
        <Flex p="30px" width="100%" flexDirection="column">
          <Center
            textStyle="bodyLight"
            minWidth="50%"
            justifyContent="flex-start"
            mb={6}
          >
            {t('ARE_YOU_SURE_TO_REMOVE_DISCOUNT_CODE')}
          </Center>

                    <Flex>
                        <Center minWidth="50%">
                            <LinkButton onClick={onClose}>
                                {t('CANCEL', {ns: 'common'})}
                            </LinkButton>
                        </Center>
                        <Button
                            minWidth="50%"
                            width="full"
                            onClick={handleRemoveCoupon}
                            isLoading={isSubmitting}
                        >
                            {t('DELETE', {ns: 'common'})}
                        </Button>
                    </Flex>
                </Flex>
            </Modal>
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(discountCodeSubmitHandler)}>
                    <Flex justifyContent="space-between" alignItems="flex-end" gap={5}>
                        <Box w="100%">
                            <Text
                                mb={0}
                                fontSize="12px"
                                fontWeight="light"
                                lineHeight="12px"
                            >
                                {t('DISCOUNT_CODE')}
                            </Text>
                            <Box position="relative" w="100%">
                                <Input
                                    height="38px"
                                    p={0}
                                    mb={0}
                                    name="code"
                                    placeholder={t('ENTER_DISCOUNT_CODE')}
                                    w="100%"
                                    fontSize="16px"
                                    lineHeight="15px"
                                    onChange={() => {
                                        if (isValidDiscountCode !== null)
                                            setIsValidDiscountCode(null)
                                    }}
                                    _disabled={{bg: 'transparent'}}
                                    isDisabled={!!data?.cart?.applied_coupons?.[0]?.code}
                                    {...(isValidDiscountCode === false
                                        ? {borderColor: '#ff003b'}
                                        : {})}
                                />
                                {(isValidDiscountCode ||
                                    !!data?.cart?.applied_coupons?.[0]?.code) && (
                                    <CheckIcon
                                        position="absolute"
                                        top="50%"
                                        right="0"
                                        transform="translateY(-50%)"
                                        color="#01c774"
                                        me="4px"
                                        fontSize={16}
                                    />
                                )}
                                {isValidDiscountCode === false && (
                                    <Text
                                        fontSize="12px"
                                        fontWeight="light"
                                        color="#ff003b"
                                        mt="4px"
                                    >
                                        {t('INVALID_CODE')}
                                    </Text>
                                )}
                            </Box>
                        </Box>
                        {isValidDiscountCode || data?.cart?.applied_coupons?.[0]?.code ? (
                            <Button
                                variant="thin"
                                width="fit-content"
                                onClick={onOpen}
                                isLoading={isSubmitting}
                            >
                                {t('DELETE', {ns: 'common'})}
                            </Button>
                        ) : (
                            <Button
                                type="submit"
                                variant="thin"
                                width="fit-content"
                                isLoading={isLoading}
                            >
                                {t('APPLY', {ns: 'common'})}
                            </Button>
                        )}
                    </Flex>
                </form>
            </FormProvider>
        </>
    )
}

export default DiscountForm
